import Backbone from 'backbone'
import template from './time-control.hbs'
import _ from 'lodash'
import select from 'scoreboard-select'
import formHelper from 'scoreboard/components/form-helper'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import numericValidator from 'scoreboard/components/validators/numeric-validator'
import numericRangeValidatorFactory from 'scoreboard/components/validators/numeric-range-validator-factory'
import KeyCodes from 'scoreboard/components/key-codes'
import { getTimeString } from 'scoreboard/components/date-time-util'
import momentHelper from 'scoreboard/components/moment-helper'

/**
 * A control containing three inputs representing hour/minute/second time
 * intervals along with a select representing am/pm periods.
 * NOTE: The inputs in this control are indeed three separate inputs which are
 * run through validation but we are not using MarkedFields here. The reason for
 * that is because these inputs work in tandem and are treated more like one
 * unbroken input in order to foster quick and easy input for the user.
 * See https://github.com/SpiderStrategies/Scoreboard/pull/33849#discussion_r544441868
 * for more info.
 */
const TimeControl = Backbone.View.extend({

	className: 'time-control form', // .form styles the labels uniformily

	events: {
		'input input': '_onInputInput',
		'change select': '_triggerChangeIfValid',
		'blur input': '_onBlurInput',
		'mousedown input': '_onInputMouseEvent',
		'mouseup input': '_onInputMouseEvent',
		'keyup input': '_onKeyupInput',
		'keydown input': '_onKeydownInput'
	},

	/**
	 * @param {Object} [options.initialValue] A JSON object representing a preset
	 * value to load into the time control upon creation.
	 * @param {TimeDisplayType} timeDisplayType standard time display type to use
	 * @param {Boolean} [options.singularLabels] If true, use singular labels rather than
	 * plural (ex. Hr vs. Hrs)
	 */
	initialize(options = {}) {
		this.options = options
		let singular = this.options.singularLabels ? '.singular' : ''

		this.timeIntervals = [{
			id: 'hour',
			key: 'calendar.hours' + singular,
			min: 1,
			max: 12
		},{
			id: 'minute',
			key: 'calendar.minutes' + singular,
			min: 0,
			max: 59
		},{
			id: 'second',
			key: 'calendar.seconds' + singular,
			min: 0,
			max: 59
		}]

		this.timeDisplayType = _.get(this.options, 'timeDisplayType')
		this.hidingSeconds = !momentHelper.isSecondsTimeType(this.timeDisplayType)
		this.hour24 = momentHelper.is24hrTimeType(this.timeDisplayType)
		this.initialValue = this.options.initialValue

		if (this.hidingSeconds) {
			this.timeIntervals.pop()
		}


		if (this.hour24) {
			this.timeIntervals[0].min = 0
			this.timeIntervals[0].max = 23
		}
	},

	render() {
		this.$el.append(template(this.context()))
		select(this.el.querySelectorAll('select'))
		this.setTime(this.initialValue)
		this.$el.toggleClass('is-hiding-seconds', this.hidingSeconds)

		// When backspacing an empty input, go to the previous input
		// Use `keydown` instead of `keyup` to capture the value before its removed.
		this.$(".time-control-section input").on('keydown', e => {
			if (e.keyCode == KeyCodes.BACKSPACE && !e.target.value){
				this.goToPrevious(e.target.name)
			}
		})

		return this
	},

	context() {
		return {
			hidingSeconds: this.hidingSeconds,
			hour24: this.hour24,
			initialValue: this.initialValue,
			timeIntervals: this.timeIntervals
		}
	},

	/**
	 * Trigger the input:change event if there's an acceptible time entered.
	 * Rules for 'acceptible' are:
	 * 1. There's a valid hour entered
	 * 2. There aren't any single digit values entered
	 */
	_triggerChangeIfValid() {
		let t = formHelper.readFromDom(this.el)
		if (this.hidingSeconds) {
			t.second = '00'
		}

		if (this._isValidInput('hour', parseInt(t.hour))
			&& t.minute.length != 1 && t.second.length != 1) {

			t.minute = t.minute == '' ? '00' : t.minute
			t.second = t.second == '' ? '00' : t.second

			// We're about to trigger an input:change event. Listeners might need to know
			// whether this input change was enough to warrant a refresh of whatever
			// it's using to display the data coming from this control.
			// That only should be happening if the time so far in this control
			// is complete enough to infer a whole time from (ex. 12::pm -> 12:00:00pm).
			// If we don't have an hour set, or if the minute or second inputs are only
			// half done, mark this input change as 'isIncomplete'
			let isIncomplete
			let currentTime = this.getTime()
			if (currentTime.hour.length < 2
				|| (currentTime.minute.length == 1 || currentTime.second.length == 1)) {
				isIncomplete = true
			}

			let time = _.extend({}, currentTime, {
				minute: t.minute,
				second: t.second
			})

			this.trigger('input:change', _.extend({}, time, {
				string: getTimeString(time, this.timeDisplayType),
				isIncomplete
			}))
		}
	},

	goToNext(current) {
		this.$(`.time-control-section.${current}`)
			.next('.time-control-section')
			.find('input, select')
			.focus()
	},

	goToPrevious(current){
		this.$(`.time-control-section.${current}`)
			.prev('.time-control-section')
			.find('input')
			.focus()
	},

	/**
	 * Accepts the input change event for all of the time inputs in the control.
	 * Ensures only valid text is entered into the inputs.
	 * @param  {Event} e The input change event
	 */
	_onInputInput(e) {
		if (this._isBlankValue(e.target.value)) {
			return
		}

		let passesValidation = val => {
			// We've entered text that isn't a number
			if (!this._isNumeric(e.target.value)
				// The resulting number is't valid for the input type
				|| !this._isValidInput(e.target.name, _.toNumber(e.target.value))) {
				return false
			}

			return true
		}

		// If we've entered text, see if it passes validation, if not, chop off
		// characters until it does.
		while (!passesValidation(e.target.value)) {
			if (this._isBlankValue(e.target.value)) {
				return
			}
			let val = e.target.value
			e.target.value = val.substring(0, val.length-1)
		}

		// If validation passed and we have two digits, move on to the next input
		if (e.target.value.length == 2) {
			this.goToNext(e.target.name)
			this._triggerChangeIfValid()
			return
		}

		// If we only have one digit in the input, but that digit is only valid
		// by itself, pad with a leading '0' and move on
		let moreValid = false
		for (var i = 0; i < 9; i++) {
			if (this._isValidInput(e.target.name, _.toNumber(e.target.value + i.toString()))) {
				moreValid = true
				break
			}
		}

		if (!moreValid) {
			e.target.value = '0' + e.target.value
			this.goToNext(e.target.name)
		}

		this._triggerChangeIfValid()
	},

	/**
	 * Pads a given input's time value with a leading zero.
	 * @param  {Object} input The input to pad
	 */
	_padTimeValue(input) {
		if (input.value.length > 1) {
			return
		}

		if (input.value == '') {
			input.value = '00'
		} else {
			input.value = '0' + input.value
		}
	},

	/**
	 * Called when an input loses it's focus. When this happens, try to format
	 * the entered value (if there is one) so that it's valid.
	 * @param  {Event} e The input's blur event
	 */
	_onBlurInput(e) {
		// Do not pad the time values or trigger any change events on input blur
		// under the following circumstances:
		// - The input blurred has no (or '0') value in it
		// - The input is already filled out (has > 1 integer)
		// - The click to blur this input was not on another input. That means
		//   that clicking away from the control will not change the value of the
		//   control, only clicking from a partially filled out input to another input will.
		if (this._isBlankValue(e.target.value) || e.target.value.length != 1
			|| this.focusedInput == e.target) {
			delete this.focusedInput
			return
		}

		this._padTimeAndValidate(e.target)
		delete this.focusedInput
	},

	// Whenever a mouse event 'down/up/etc' happens on an input, be sure to save that
	// input as this.focusedInput.
	_onInputMouseEvent(e) {
		this.focusedInput = e.target
	},

	// When we tab while focused on an input and that input has a partial value in it,
	// go ahead and pad that input out and validate the resulting time.
	_onKeydownInput(e) {
		if (e.keyCode == KeyCodes.TAB && e.target.value.length == 1) {
			this._padTimeAndValidate(e.target)
		}
	},

	// If we've tabbed from one input to another, be sure to update
	// this.focusedInput. This is done on keyup because the input hasn't been
	// changed yet on keydown.
	_onKeyupInput(e) {
		if (e.keyCode == KeyCodes.TAB && document.activeElement.nodeName == 'INPUT') {
			this.focusedInput = e.target
		}
	},

	/**
	 * Pad out the time value on the given input and conditionally trigget input:change
	 * @param {Object} input The dom input which is to be adjusted.
	 */
	_padTimeAndValidate(input) {
		this._padTimeValue(input)

		// If we've just padded out an input, check to see if the whole control
		// is valid and be sure to trigger the change event in that case
		let t = formHelper.readFromDom(this.el)
		if (this._isValidInput('hour', parseInt(t.hour))
			&& this._isValidInput('minute', parseInt(t.minute))
			&& this._isValidInput('second', parseInt(t.second))) {
			this.trigger('input:change', this.getTime())
		}
	},

	/**
	 * Takes an input value and determines whether it is 'blank' or not (whether
	 * or not it has a time-worthy value)
	 * @param  {Number} v The value to test
	 * @return {Boolean}   True if the value is blank, else false.
	 */
	_isBlankValue(v) {
		v = _.trim(v)
		return !_.isNil(requiredValidator(v)) || v == '0'
	},

	_isNumeric(n) {
		return _.isNil(numericValidator(n))
	},

	_isWithinRange(min, max, n) {
		return _.isNil(numericRangeValidatorFactory(min, max)(n))
	},

	/**
	 * Determines whether a given input is valid for a given input type (hour, min, sec, etc)
	 * @param  {string} type  A string indicating the kind of input we're dealing with.
	 * @param  {Number} input The numeric input to validate
	 * @return {Boolean}       True if the input is valid for the type, else false.
	 */
	_isValidInput(type, input) {
		if (!_.isFinite(input)) {
			return false
		}

		let interval
		this.timeIntervals.forEach(i => {
			if (i.id == type) {
				interval = i
			}
		})

		return this._isWithinRange(interval.min, interval.max, input)
	},

	getTime() {
		let time = formHelper.readFromDom(this.el)
		if (this.hidingSeconds) {
			time.second = '00'
		}

		return  _.extend({}, time, {
			timeType: this.hour24 ? 'military' : 'standard',
			string: getTimeString(time, this.timeDisplayType)
		})
	},

	setTime(time) {
		if (_.isNil(time)) return

		if (this.hidingSeconds) {
			time.second = '00'
		}

		formHelper.writeToDom(time, this.el)
		this.$('input[type="text"]').each((i, input) => this._padTimeValue(input))
	},

	clear() {
		this.$('input[type="text"]').each((i, input) => input.value = '')
	},

	/**
	 * Recieve focus to this control by selecting the first input which appears
	 * within it.
	 */
	focus() {
		this.$('.time-control-section:first-child input[type="text"]').focus()
	}
})

export default TimeControl

TimeControl.__filename__ = 'components/time-control.js'
